import React, { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import type { DatasetOption } from './hooks';
import { Icon } from '../../components/ui';

/**
 * Searchable list of the org's datasets. Mirrors {@link TriggerPickerContent}'s
 * shape so the two steps of the wizard read as one flow.
 */
export const DatasetPickerContent: React.FC<{
  datasets: DatasetOption[];
  selectedDatasetId: string | null;
  onSelect: (datasetId: string) => void;
}> = ({ datasets, selectedDatasetId, onSelect }) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return datasets;
    }
    return datasets.filter((dataset) => dataset.name.toLowerCase().includes(q));
  }, [datasets, query]);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search datasets..."
          className="h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          {datasets.length === 0
            ? 'No datasets yet. Upload a CSV or create one first.'
            : 'No datasets match your search.'}
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {filtered.map((dataset) => {
            const selected = dataset.id === selectedDatasetId;
            return (
              <button
                key={dataset.id}
                type="button"
                onClick={() => onSelect(dataset.id)}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-left',
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:bg-muted'
                )}
              >
                <Icon
                  icon={'dataset'}
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{dataset.name}</p>
                  {dataset.folderName !== null &&
                  dataset.folderName !== undefined ? (
                    <p className="truncate text-xs text-muted-foreground">
                      {dataset.folderName}
                    </p>
                  ) : null}
                </div>
                {selected ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
